import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useSiteImage } from '@/hooks/useSiteImage';

export default function CTASplitSection() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const imageUrl = useSiteImage('home.ctaSplit.image', 'https://public.readdy.ai/ai/img_res/b552eaec-90a2-42dc-82b6-db1e6fc4215d.png');

  return (
    <section className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Left panel: Dark */}
        <div className="relative bg-[#1A1A1A] py-20 md:py-28 px-6 md:px-12 lg:px-16 flex flex-col items-center text-center gap-6 overflow-hidden">
          {/* Subtle gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] via-[#1A1A1A] to-[#1B3A5C]/15 pointer-events-none" />

          {/* Product image */}
          <div className="relative z-10 w-48 md:w-56">
            <img
              src={imageUrl}
              alt="Rx.K Seoul 3-Step Set"
              className="w-full h-auto object-contain opacity-90"
            />
          </div>

          {/* Brand mark */}
          <div className="relative z-10 flex items-center gap-0.5">
            <span className="font-sans text-sm font-bold text-[#8A8A8A]">Rx</span>
            <span className="font-sans text-sm font-bold text-[#7B1113]">+.</span>
            <span className="font-sans text-[10px] font-bold tracking-[0.15em] text-[#8A8A8A] uppercase">
              K SEOUL
            </span>
          </div>

          <h2 className="relative z-10 font-serif text-xl md:text-2xl lg:text-3xl font-semibold text-white leading-[1.2]">
            {t('home.ctaSplit.left.heading')}
          </h2>
        </div>

        {/* Right panel: Light */}
        <div className="bg-[#F0EFED] py-20 md:py-28 px-6 md:px-12 lg:px-16 flex flex-col gap-6">
          <h2 className="font-serif text-xl md:text-2xl lg:text-3xl font-semibold text-[#2A2A2A] leading-[1.2]">
            {t('home.ctaSplit.right.heading')}
          </h2>

          <p className="text-sm text-[#8A8A8A] font-light leading-[1.8] max-w-md">
            {t('home.ctaSplit.right.body')}
          </p>

          {/* Mini step visual */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-[#8A8A8A] font-light">
            <span className="px-3 py-1.5 rounded-full bg-white border border-[#E8E8E6] text-[10px] tracking-wider">
              {t('home.ctaSplit.right.step1')}
            </span>
            <span className="text-[#8A8A8A]/40 hidden sm:inline">&rarr;</span>
            <span className="px-3 py-1.5 rounded-full bg-white border border-[#E8E8E6] text-[10px] tracking-wider">
              {t('home.ctaSplit.right.step2')}
            </span>
            <span className="text-[#8A8A8A]/40 hidden sm:inline">&rarr;</span>
            <span className="px-3 py-1.5 rounded-full bg-white border border-[#E8E8E6] text-[10px] tracking-wider">
              {t('home.ctaSplit.right.step3')}
            </span>
          </div>

          {/* CTAs */}
          <div className="flex items-center gap-3 flex-wrap mt-1">
            <Link
              to={prefix + '/products'}
              className="px-5 py-3 bg-[#7B1113] text-white text-[11px] tracking-[0.12em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
            >
              {t('home.ctaSplit.right.ctaShop')}
            </Link>
            <Link
              to={prefix + '/stores'}
              className="px-5 py-3 border border-[#2A2A2A]/20 text-[#2A2A2A] text-[11px] tracking-[0.12em] uppercase rounded-full hover:bg-[#2A2A2A] hover:text-white transition-all cursor-pointer text-center"
            >
              {t('home.ctaSplit.right.ctaWhere')}
            </Link>
          </div>

          {/* Bottom stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-6 border-t border-[#2A2A2A]/10">
            {[
              { label: t('home.ctaSplit.stat1.label'), value: t('home.ctaSplit.stat1.value') },
              { label: t('home.ctaSplit.stat3.label'), value: t('home.ctaSplit.stat3.value') },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1">
                <span className="text-[10px] tracking-[0.12em] uppercase text-[#8A8A8A] font-light">
                  {stat.label}
                </span>
                <span className="text-sm font-medium text-[#2A2A2A]">{stat.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}